"use client";

import { motion } from "motion/react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// =============================================================================
// Building blocks of the "How I work" visuals. Every size is expressed in
// container query units (cqw) so that the miniature apps scale with the
// window width.
// =============================================================================

type AppWindowProps = {
  children: ReactNode;
  className?: string;
  /** Dark chrome (code editor, video call) */
  dark?: boolean;
};

/**
 * Rounded app window with a soft floor shadow, like the Figma visuals. The
 * miniature apps are pictures: inert and hidden from assistive technologies
 * (the steps carry the content, their tiny texts are decoration).
 */
export function AppWindow({
  children,
  className,
  dark = false,
}: AppWindowProps) {
  return (
    <div inert aria-hidden className="@container relative w-full">
      <div
        className={cn(
          "relative aspect-[16/9] w-full overflow-hidden rounded-[2.4cqw] shadow-[0_2cqw_6cqw_-1cqw_rgb(0_0_0/0.45)] ring-1",
          dark
            ? "bg-[#0d1117] text-[#e6edf3] ring-white/10"
            : "bg-white text-[#141414] ring-black/5",
          className
        )}
      >
        {children}
      </div>
      {/* Blurred floor reflection */}
      <div
        aria-hidden
        className="absolute inset-x-[2%] -bottom-[4cqw] h-[2cqw] rounded-full bg-black/40 blur-[1.6cqw] dark:bg-black/60"
      />
    </div>
  );
}

// =============================================================================

const PALETTE = [
  "#F97362",
  "#F6B23E",
  "#7BC67E",
  "#5AA9E6",
  "#A78BFA",
  "#F472B6",
  "#34D399",
  "#FB923C",
  "#60A5FA",
];

type AvatarProps = {
  name: string;
  className?: string;
  /** Force a palette color */
  colorIndex?: number;
};

/**
 * Initials avatar with a deterministic color.
 */
export function Avatar({ name, className, colorIndex }: AvatarProps) {
  const initials = name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const index =
    colorIndex ??
    [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      PALETTE.length;

  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-full font-semibold text-white",
        className
      )}
      style={{
        background: `linear-gradient(135deg, ${PALETTE[index]}, ${PALETTE[(index + 3) % PALETTE.length]})`,
      }}
    >
      {initials}
    </span>
  );
}

// =============================================================================

/**
 * Three bouncing dots of a "is typing" bubble.
 */
export function TypingDots({ className }: { className?: string }) {
  return (
    <span className={cn("flex items-center gap-[0.45cqw]", className)}>
      {[0, 1, 2].map((dot) => (
        <motion.span
          key={dot}
          className="block size-[0.75cqw] rounded-full bg-current opacity-60"
          animate={{ y: ["0%", "-60%", "0%"], opacity: [0.4, 1, 0.4] }}
          transition={{
            duration: 0.9,
            repeat: Infinity,
            delay: dot * 0.15,
            ease: "easeInOut",
          }}
        />
      ))}
    </span>
  );
}

// =============================================================================

type TypewriterProps = {
  text: string;
  /** Characters per second */
  speed?: number;
  delay?: number;
  className?: string;
  caret?: boolean;
};

/**
 * Reveals a text character by character (steps easing on a clip-path).
 */
export function Typewriter({
  text,
  speed = 38,
  delay = 0,
  className,
  caret = false,
}: TypewriterProps) {
  const duration = Math.max(0.2, text.length / speed);
  const steps = Math.max(1, text.length);

  return (
    <span className={cn("relative inline-block", className)}>
      <motion.span
        className="inline-block whitespace-pre-wrap"
        initial={{ clipPath: "inset(0 100% 0 0)" }}
        animate={{ clipPath: "inset(0 0% 0 0)" }}
        transition={{
          duration,
          delay,
          ease: (t: number) => Math.floor(t * steps) / steps,
        }}
      >
        {text}
      </motion.span>
      {caret && (
        <span className="ml-[0.1em] inline-block h-[1em] w-[0.08em] translate-y-[0.12em] animate-caret bg-current" />
      )}
    </span>
  );
}

// =============================================================================

type CursorProps = {
  label?: string;
  color?: string;
  className?: string;
};

/**
 * Collaborative cursor (arrow + name tag).
 */
export function Cursor({ label, color = "#7C3AED", className }: CursorProps) {
  return (
    <span className={cn("pointer-events-none absolute z-20 block", className)}>
      <svg viewBox="0 0 16 16" className="size-[2.2cqw] drop-shadow-sm">
        <path
          d="M1.5 1.2 14 6.6l-5.4 1.7-1.9 5.6Z"
          fill={color}
          stroke="#fff"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
      {label && (
        <span
          className="absolute top-[2cqw] left-[1.6cqw] rounded-[0.6cqw] px-[0.8cqw] py-[0.25cqw] text-[1.1cqw] font-semibold whitespace-nowrap text-white"
          style={{ background: color }}
        >
          {label}
        </span>
      )}
    </span>
  );
}

// =============================================================================
